import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Components, Theme, Utils, translate,
} from 'versafleet-core';
import ReduxActions from 'reduxActions';
import moment from 'moment';
import classNames from 'classnames';

const READ_TIMEOUT = 5000;

class NotificationLineItem extends React.Component {
  componentDidMount() {
    if (!this.props.data.read) {
      this._autoReadNotification();
    }
  }

  componentWillUnmount() {
    // Prevent notifications from being read if the user navigates away
    // from the page within 5 seconds
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  _readNotification() {
    this.props.dispatch(ReduxActions.notification.readNotification(this.props.data.id));
  }

  // Auto-read an unread notification after 5 seconds
  _autoReadNotification() {
    this.timer = setTimeout(() => {
      this._readNotification();
    }, READ_TIMEOUT);
  }

  _routeContent() {
    switch (this.props.data.category) {
      case 'job_completed':
        return this._renderCompletedJob();
      case 'job_rejected':
        return this._renderRejectedJob();
      case 'job_accepted':
        return this._renderAcceptedJob();
      case 'job_submitted':
        return this._renderJob('submitted');
      case 'job_revoked':
        return this._renderJob('retracted');
      case 'job_cancelled':
        return this._renderJob('cancelled');
      case 'task_acknowledged':
        return this._renderTask('acknowledged');
      case 'task_declined':
        return this._renderTask('declined');
      case 'task_started':
        return this._renderTask('started');
      case 'task_failed':
        return this._renderTask('failed');
      case 'task_rejected':
        return this._renderTask('rejected');
      case 'task_successful':
        return this._renderTask('succeeded');
      case 'task_collected':
        return this._renderTask('collected');
      case 'task_arrived':
        return this._renderTask('arrived');
      case 'task_cancelled':
        return this._renderTask('cancelled');
      case 'task_notificator':
        return this._renderNotificator();
      default:
        return {};
    }
  }

  _renderTask(keyword) {
    const iconClassnamesMap = {
      acknowledged: 'versa-driver',
      started: 'versa-otw',
      declined: 'versa-warning',
      failed: 'versa-warning',
      cancelled: 'versa-danger',
      succeeded: 'versa-completed',
      collected: 'versa-completed',
      arrived: 'versa-arrived',
    };

    const innerIconColorMap = {
      acknowledged: Theme.Color.$bluelighter1,
      started: Theme.Color.$bluelighter1,
      declined: Theme.Color.$bluelighter1,
      failed: Theme.Color.$bluelighter1,
      cancelled: Theme.Color.$bluelighter1,
      succeeded: Theme.Color.$white,
      collected: Theme.Color.$green,
      arrived: Theme.Color.$white,
    };

    const outerIconColorMap = {
      acknowledged: Theme.Color.$grey,
      started: Theme.Color.$grey,
      declined: Theme.Color.$red,
      failed: Theme.Color.$red,
      cancelled: Theme.Color.$red,
      succeeded: Theme.Color.$green,
      collected: Theme.Color.$green,
      arrived: Theme.Color.$green,
    };

    const shouldShowPartial = this.props.data.data.task.is_partial_success && keyword === 'succeeded';

    const replaceMap = {
      partial: shouldShowPartial ? (
        <span className="vf-notification__mobile-line-item__bold-span">
          {this.props.strings.partial}
        </span>
      ) : false,
      driverName: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {Utils.Helper.isNullOrUndefined(this.props.data.data.task.task_assignment.driver) ? ''
            : this.props.data.data.task.task_assignment.driver.name}
        </span>
      ),
      keyword: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {this.props.strings.driverConstants[keyword]}
        </span>
      ),
      guid: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {this.props.data.data.task.guid}
        </span>
      ),
      customerName: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {this.props.data.data.task.job.customer.name}
        </span>
      ),
    };

    return {
      icon: (
        <Components.CircleIcon
          className={keyword === 'collected' ? 'vf-notification__mobile-line-item__collected-icon' : null}
          iconClassName={shouldShowPartial ? 'versa-completedpartially' : iconClassnamesMap[keyword]}
          innerIconColor={innerIconColorMap[keyword]}
          outerIconColor={outerIconColorMap[keyword]}
        />
      ),
      content: (
        Utils.Helper.isNullOrUndefined(this.props.data.data.task.task_assignment.driver)
          ? Utils.TranslationHelper.replaceTag(this.props.strings.taskWithoutDriver, replaceMap)
          : Utils.TranslationHelper.replaceTag(this.props.strings.taskWithDriver, replaceMap)
      ),
      redirectUrl: `/tasks/${this.props.data.data.task.id}`,
    };
  }

  _renderNotificator() {
    const { notificator, task } = this.props.data.data;
    const { strings } = this.props;

    const absoluteMinutes = Math.abs(notificator.minutes);
    const shouldShowPartial = this.props.data.data.task.is_partial_success
      && notificator.action_name === 'succeeded';

    const replaceMap = {
      partial: shouldShowPartial ? (
        <span className="vf-notification__mobile-line-item__bold-span">
          {this.props.strings.partial}
        </span>
      ) : false,
      taskGUID: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {task.guid}
        </span>
      ),
      hasBeen: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {notificator.is_been ? strings.isBeen : strings.isNotBeen}
        </span>
      ),
      actionName: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {strings.notificatorConstants[notificator.action_name]}
        </span>
      ),
      hourCount: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {Math.floor(absoluteMinutes / 60)}
        </span>
      ),
      hours: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {strings.hours}
        </span>
      ),
      minuteCount: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {absoluteMinutes % 60}
        </span>
      ),
      minutes: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {strings.minutes}
        </span>
      ),
      timePreposition: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {parseInt(notificator.minutes, 10) < 0 ? strings.after : strings.before}
        </span>
      ),
      time: (
        <span>
          {moment(task[notificator.trigger_name]).format('HH:mm:ss, DD MMM YYYY')}
        </span>
      ),
    };

    return {
      icon: (
        <Components.CircleIcon
          iconClassName="versa-notifications"
          iconText=""
        />
      ),
      content: (
        notificator.minutes >= 60
          ? Utils.TranslationHelper.replaceTag(strings.notificatorWithHours, replaceMap)
          : Utils.TranslationHelper.replaceTag(strings.notificatorWithoutHours, replaceMap)

      ),
      redirectUrl: `/tasks/${this.props.data.data.task.id}`,
    };
  }

  _renderRejectedJob() {
    const replaceMap = {
      guid: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {this.props.data.data.job.guid}
        </span>
      ),
      reason: this.props.data.data ? (
        <span className="vf-notification__mobile-line-item__bold-span">
          {this.props.data.data && this.props.data.data.myjobs_rejection_note}
        </span>
      ) : false,
    };

    return {
      icon: (
        <Components.CircleIcon
          iconClassName="versa-warning"
          iconText=""
          innerIconColor={Theme.Color.$red}
          outerIconColor={Theme.Color.$redlighter}
        />
      ),
      content: !Utils.Helper.isNonEmptyString(this.props.data.data.myjobs_rejection_note)
        ? Utils.TranslationHelper.replaceTag(
          this.props.strings.rejectedJobWithoutReason, replaceMap,
        )
        : Utils.TranslationHelper.replaceTag(this.props.strings.rejectedJob, replaceMap),
      redirectUrl: `/jobs/${this.props.data.data.job.id}`,
    };
  }

  _renderAcceptedJob() {
    const replaceMap = {
      guid: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {this.props.data.data.job.guid}
        </span>
      ),
    };

    return {
      icon: (
        <Components.CircleIcon
          iconClassName="versa-completed"
          iconText=""
          innerIconColor={Theme.Color.$white}
          outerIconColor={Theme.Color.$green}
        />
      ),
      content: Utils.TranslationHelper.replaceTag(this.props.strings.acceptedJob, replaceMap),
      redirectUrl: `/jobs/${this.props.data.data.job.id}`,
    };
  }

  _renderCompletedJob() {
    const replaceMap = {
      guid: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {this.props.data.data.job.guid}
        </span>
      ),
      customerName: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {this.props.data.data.job.customer.name}
        </span>
      ),
    };

    return {
      icon: (
        <Components.CircleIcon
          iconClassName="versa-business"
          iconText=""
        />
      ),
      content: Utils.TranslationHelper.replaceTag(this.props.strings.completedJob, replaceMap),
      redirectUrl: `/jobs/${this.props.data.data.job.id}`,
    };
  }

  _renderJob(keyword) {
    const replaceMap = {
      customerName: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {`${this.props.data.data.job.customer.name} `}
        </span>
      ),
      keyword: (
        <span className="vf-notification__mobile-line-item__bold-span">
          {this.props.strings.constants[keyword]}
        </span>
      ),
      guid: (
        <span className="vf-notification__mobile-line-item__blue-span">
          {` ${this.props.data.data.job.guid} `}
        </span>
      ),
    };

    return {
      icon: (
        <Components.CircleIcon
          iconClassName="versa-business"
          iconText=""
        />
      ),
      content: Utils.TranslationHelper.replaceTag(this.props.strings.job, replaceMap),
      redirectUrl: `/jobs/${this.props.data.data.job.id}`,
    };
  }

  render() {
    const { icon, content, redirectUrl } = this._routeContent();

    const className = classNames({
      'vf-notification__mobile-line-item': true,
      'vf-notification__mobile-line-item--unread': !this.props.data.read,
    });

    return (
      <li
        className={className}
        id="vf-mobile-notification__line-item"
      >
        <div
          className="vf-notification__mobile-line-item__link"
          onClick={() => {
            this._readNotification();
            Utils.Router.transitionTo(redirectUrl);
          }}
        >
          <div className="vf-notification__mobile-line-item__icon">
            {icon}
          </div>
          <div className="vf-notification__mobile-line-item__content">
            {content}
          </div>
          <div className="vf-notification__mobile-line-item__time">
            {moment(this.props.data.created_at).fromNow(true)}
          </div>
        </div>
      </li>
    );
  }
}

NotificationLineItem.propTypes = {
  data: PropTypes.shape({
    category: PropTypes.string,
    data: PropTypes.shape({
      job: PropTypes.shape({
        customer: PropTypes.shape({
          name: PropTypes.string,
        }),
        guid: PropTypes.string,
        id: PropTypes.number,
      }),
      myjobs_rejection_note: PropTypes.string,
      notificator: PropTypes.shape({
        action_name: PropTypes.string,
        is_been: PropTypes.bool,
        minutes: PropTypes.number,
        trigger_name: PropTypes.string,
      }),
      task: PropTypes.shape({
        guid: PropTypes.string,
        id: PropTypes.number,
        is_partial_success: PropTypes.bool,
        job: PropTypes.shape({
          customer: PropTypes.shape({
            name: PropTypes.string,
          }),
        }),
        task_assignment: PropTypes.shape({
          driver: PropTypes.shape({
            name: PropTypes.string,
          }),
        }),
      }),
    }),
    id: PropTypes.number,
    read: PropTypes.bool,
    created_at: PropTypes.string,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  strings: PropTypes.shape({
    constants: PropTypes.shape(),
    job: PropTypes.shape(),
    completedJob: PropTypes.string,
    acceptedJob: PropTypes.string,
    rejectedJob: PropTypes.string,
    rejectedJobWithoutReason: PropTypes.string,
    driverConstants: PropTypes.shape(),
    taskWithoutDriver: PropTypes.string,
    taskWithDriver: PropTypes.string,
    partial: PropTypes.string,
    isBeen: PropTypes.string,
    isNotBeen: PropTypes.string,
    hours: PropTypes.string,
    minutes: PropTypes.string,
    after: PropTypes.string,
    before: PropTypes.string,
    notificatorWithHours: PropTypes.string,
    notificatorWithoutHours: PropTypes.string,
    notificatorConstants: PropTypes.shape(),
  }).isRequired,
};

export default connect()(translate('Notification')(NotificationLineItem));
